import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import { cn } from '@/lib/cn'

type ConfirmationTone = 'danger' | 'warning'

export interface ConfirmationDialogProps {
  cancelLabel: string
  confirmLabel: string
  description: string
  isConfirming?: boolean
  onCancel: () => void
  onConfirm: () => void
  open: boolean
  title: string
  tone?: ConfirmationTone
}

function WarningIcon({ tone }: { tone: ConfirmationTone }) {
  return (
    <span
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded-full',
        tone === 'danger'
          ? 'bg-[rgba(217,45,32,0.1)] text-[var(--color-danger)]'
          : 'bg-[rgba(245,158,11,0.14)] text-[#b45309]',
      )}
    >
      <svg aria-hidden className="size-5" fill="none" viewBox="0 0 20 20">
        <path
          d="M10 6.5v4.25m0 2.75h.01M8.43 3.4 2.3 14.05A1.7 1.7 0 0 0 3.78 16.6h12.44a1.7 1.7 0 0 0 1.48-2.55L11.57 3.4a1.81 1.81 0 0 0-3.14 0Z"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.6"
        />
      </svg>
    </span>
  )
}

export function ConfirmationDialog({
  cancelLabel,
  confirmLabel,
  description,
  isConfirming = false,
  onCancel,
  onConfirm,
  open,
  title,
  tone = 'warning',
}: ConfirmationDialogProps) {
  const requestCancel = () => {
    if (!isConfirming) onCancel()
  }

  return (
    <Modal
      className="max-w-[460px]"
      closeLabel={`${cancelLabel}: ${title}`}
      footer={
        <>
          <Button disabled={isConfirming} onClick={requestCancel} variant="secondary">
            {cancelLabel}
          </Button>
          <Button
            isLoading={isConfirming}
            onClick={onConfirm}
            variant={tone === 'danger' ? 'danger' : 'primary'}
          >
            {confirmLabel}
          </Button>
        </>
      }
      onClose={requestCancel}
      open={open}
      title={title}
    >
      <div className="flex items-start gap-3.5 pt-1">
        <WarningIcon tone={tone} />
        <p className="pt-1.5 text-sm leading-6 text-[var(--color-text-muted)]">{description}</p>
      </div>
    </Modal>
  )
}
